/**
 * Bonding Curve Estimator — Meteora DBC concentrated liquidity math.
 *
 * Frontend version for real-time token allocation estimates as users
 * enter deposit amounts. Uses the same math as the backend estimator.
 *
 * Keep in sync with: backend/src/services/curveEstimator.ts
 */

// ─── Curve Parameters (derived once from constants) ──────────────────────────

export interface CurveParams {
    sqrtStartPrice: number;
    sqrtMigrationPrice: number;
    liquidity: number;
    pctSupplyOnMigration: number;
    migrationQuoteAmount: number;
}

/**
 * Derive the bonding curve parameters from DBC constants.
 */
export function getCurveParams(
    initialMcSol: number,
    migrationMcSol: number,
    totalSupply: number,
): CurveParams {
    const sqrtRatio = Math.sqrt(initialMcSol / migrationMcSol);
    const pctSupplyOnMigration = (100 * sqrtRatio) / (1 + sqrtRatio);
    const migrationQuoteAmount = (migrationMcSol * pctSupplyOnMigration) / 100;

    const startPrice = initialMcSol / totalSupply;
    const migrationPrice = migrationMcSol / totalSupply;

    const sqrtStartPrice = Math.sqrt(startPrice);
    const sqrtMigrationPrice = Math.sqrt(migrationPrice);

    const liquidity = migrationQuoteAmount / (sqrtMigrationPrice - sqrtStartPrice);

    return {
        sqrtStartPrice,
        sqrtMigrationPrice,
        liquidity,
        pctSupplyOnMigration,
        migrationQuoteAmount,
    };
}

// ─── Estimation Functions ────────────────────────────────────────────────────

export interface SwapEstimate {
    tokensOut: number;
    newSqrtPrice: number;
    avgPrice: number;
}

/**
 * Estimate tokens received for a given SOL deposit at the current curve position.
 *
 * @param solAmount         SOL being deposited
 * @param currentSqrtPrice  Current position on the curve (from cabal.curveEstimate.sqrtPrice)
 * @param params            Curve params
 * @param feeReserveSol     SOL reserved per member for fees (deducted before swap)
 */
export function estimateTokensForDeposit(
    solAmount: number,
    currentSqrtPrice: number,
    params: CurveParams,
    feeReserveSol: number = 0,
): SwapEstimate {
    const solForSwap = feeReserveSol > 0
        ? Math.max(solAmount - feeReserveSol, 0)
        : solAmount;

    if (solForSwap <= 0 || currentSqrtPrice <= 0 || params.liquidity <= 0) {
        return { tokensOut: 0, newSqrtPrice: currentSqrtPrice, avgPrice: 0 };
    }

    // Cap at migration price
    const maxSolToMigration = params.liquidity * (params.sqrtMigrationPrice - currentSqrtPrice);
    const effectiveSol = Math.min(solForSwap, maxSolToMigration);

    if (effectiveSol <= 0) {
        return { tokensOut: 0, newSqrtPrice: currentSqrtPrice, avgPrice: 0 };
    }

    // newSqrtPrice = currentSqrtPrice + solForSwap / L
    const newSqrtPrice = currentSqrtPrice + effectiveSol / params.liquidity;

    // tokensOut = L * (newSqrtP - currentSqrtP) / (newSqrtP * currentSqrtP)
    const tokensOut =
        (params.liquidity * (newSqrtPrice - currentSqrtPrice)) /
        (newSqrtPrice * currentSqrtPrice);

    const avgPrice = tokensOut > 0 ? effectiveSol / tokensOut : 0;

    return { tokensOut, newSqrtPrice, avgPrice };
}

/**
 * Format token amount for display (e.g., "12.5M", "450K", "1,234").
 */
export function formatTokenAmount(tokens: number): string {
    if (tokens >= 1_000_000) {
        return `${(tokens / 1_000_000).toFixed(2)}M`;
    }
    if (tokens >= 1_000) {
        return `${(tokens / 1_000).toFixed(1)}K`;
    }
    return Math.round(tokens).toLocaleString();
}
